import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import GustoConnectYourAccount from '../components/GustoConnectYourAccount';
import GustoConnectionInstructions from '../components/GustoConnectionInstructions';
import { IVendorData, IServerErrorResponse } from '../../../types';
import { IGustoLinkTokenResponse, useGenerateGustoLinkToken } from '../../../hooks/query/useGenerateGustoLinkToken';
import { useStore } from '../../../store';
import { useConnectGusto } from '../../../hooks/mutations/useConnectGusto';
import { useDisconnectGusto } from '../../../hooks/mutations/useDisconnectGusto';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import GustoConnected from '../components/GustoConnected';
import GustoModal, { MODAL_TYPES } from '../components/GustoModal';

interface IGustoAppProps {
  gustoData: IVendorData;
  isLoading: boolean;
}

const GUSTO_ERRORS = {
  PAYROLL_NOT_BUSINESS_CUSTOMER: 60001,
  PAYROLL_NOT_FOUND: 60002,
  PAYROLL_INVALID_TOKEN: 60003,
};

const GustoApp = ({ gustoData, isLoading }: IGustoAppProps) => {
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<string>(MODAL_TYPES.NONE);
  const [modalData, setModalData] = useState<{ title: string; subtitle: string }>({ title: '', subtitle: '' });
  const { customerId } = useStore((state) => state.user);
  const { payrollCompanyId } = useStore((state) => state.getSummaryData());
  const isGustoConnected = !!payrollCompanyId?.[0];
  const { t } = useTranslation();
  const {
    refetch: updateSummary,
    isLoading: isLoadingSummary,
    isRefetching: isRefetchingSummary,
  } = useAccountSummaryQuery();
  const handleAPIErrors = (error: IServerErrorResponse) => {
    const errData = error?.response?.data?.errors?.[0];
    const errCode = errData?.code;
    const errMsg = errData?.localizedMessage;
    if ([GUSTO_ERRORS.PAYROLL_NOT_BUSINESS_CUSTOMER, GUSTO_ERRORS.PAYROLL_INVALID_TOKEN].includes(errCode)) {
      setModalData({ title: t('GUSTO.ERRORS.COULD_NOT_CONNECT.TITLE'), subtitle: errMsg || '' });
      setShowModal(MODAL_TYPES.COULD_NOT_CONNECT);
    } else {
      setModalData({ title: t('GENERAL_ERROR.TITLE'), subtitle: errMsg || '' });
      setShowModal(MODAL_TYPES.COULD_NOT_CONNECT);
    }
  };
  const { refetch: fetchGustoLinkToken } = useGenerateGustoLinkToken(
    { customerId },
    (data: IGustoLinkTokenResponse) => {
      const redirectUrl = data?.url;
      if (redirectUrl) window.location.href = redirectUrl;
    },
    (error: IServerErrorResponse) => {
      setIsButtonLoading(false);
      handleAPIErrors(error);
    },
  );
  const searchParams = useStore((state) => state.searchParams);
  const setSearchParams = useStore((state) => state.setSearchParams);
  const urlSearchParams = useMemo(() => new URLSearchParams(searchParams), [searchParams]);
  const { mutate: connectGusto, isLoading: isLoadingGustoConnect } = useConnectGusto(
    { customerId },
    () => {
      setShowModal(MODAL_TYPES.SUCCESS_CONNECT);
    },
    (error: any) => {
      handleAPIErrors(error);
    },
  );
  const { mutate: disconnectGusto, isLoading: isLoadingGustoDisconnect } = useDisconnectGusto(
    { customerId, companyId: payrollCompanyId },
    () => {
      setShowModal(MODAL_TYPES.SUCCESS_DISCONNECT);
    },
  );
  const showSkeletonLoader =
    isLoading || isLoadingGustoConnect || isLoadingGustoDisconnect || isLoadingSummary || isRefetchingSummary;

  const handleCloseModal = () => {
    if (showModal === MODAL_TYPES.SUCCESS_CONNECT || showModal === MODAL_TYPES.SUCCESS_DISCONNECT) updateSummary();
    setShowModal(MODAL_TYPES.NONE);
  };
  const getParamByName = (paramName: string) => {
    try {
      return decodeURIComponent(urlSearchParams.get(paramName) || '');
    } catch (e) {
      console.error(e);
      return '';
    }
  };
  useEffect(() => {
    if (!isGustoConnected) {
      const gustoCode = getParamByName('code');
      const gustoError = getParamByName('error');
      const gustoState = getParamByName('state');
      if (gustoCode && gustoState) {
        connectGusto({ code: gustoCode, state: gustoState });
        setSearchParams('');
      } else if (gustoError) {
        setShowModal(MODAL_TYPES.COULD_NOT_CONNECT);
        setSearchParams('');
      }
    }
  }, [urlSearchParams]);

  const handleConnectClick = () => {
    if (!isGustoConnected) {
      setIsButtonLoading(true);
      fetchGustoLinkToken();
    }
  };
  const handleDisconnectClick = () => {
    if (customerId && isGustoConnected) {
      disconnectGusto();
    }
  };

  return (
    <div className='flex gap-[30px]'>
      {showSkeletonLoader && (
        <div className='w-[552px] h-[568px] bg-bw00 rounded-md p-10'>
          <Skeleton className='my-3' count={9} />
        </div>
      )}
      {!showSkeletonLoader && !isGustoConnected && !isLoadingGustoConnect && (
        <>
          <GustoConnectYourAccount onConnectClick={handleConnectClick} isLoading={isButtonLoading} />
          <GustoConnectionInstructions headerText='Step 02' data={gustoData} />
        </>
      )}
      {!showSkeletonLoader && isGustoConnected && (
        <>
          <GustoConnectionInstructions data={gustoData} showLogo />
          <GustoConnected onDisconnectClick={handleDisconnectClick} isLoading={isLoading} />
        </>
      )}
      {showModal !== MODAL_TYPES.NONE && (
        <GustoModal
          onClose={handleCloseModal}
          modalType={showModal}
          title={modalData?.title}
          subtitle={modalData?.subtitle}
        />
      )}
    </div>
  );
};

export default GustoApp;
